// Strip redundant markdown fragments from assistant chat prose when the
// matching structured itinerary card is rendered alongside. Removes:
//   - whole "- **Description**: …" (or "- Description: …") bullet lines,
//     at any indent depth, since the description is shown in the item
//     card on the timeline;
//   - stray "### Day N: Title" headers that duplicate the itinerary
//     card's day list;
//   - leading/trailing whitespace and collapsed blank-line runs.
export function cleanChatMarkdown(input: string): string {
  if (!input) return "";
  let s = input;
  // Drop whole lines that are pure label sub-items leaking from the
  // model's template. Matches with or without leading bullet, at any
  // indent depth. These labels are already shown as first-class fields
  // on each item card on the timeline, so they're pure noise in prose.
  const redundantLabels = [
    "description",
    "location",
    "coordinates",
    "coords",
    "cost",
    "price",
    "duration",
    "time",
    "address",
    "place",
    "rating",
  ];
  const labelAlt = redundantLabels.join("|");
  s = s.replace(
    new RegExp(
      `^[\\t ]*[-*+]?[\\t ]*\\**(?:${labelAlt})\\**\\s*:.*(?:\\r?\\n|$)`,
      "gim"
    ),
    ""
  );
  // Drop "### Day N: ..." headers — the card already lists them.
  s = s.replace(/^#{1,6}[\t ]*day[\t ]+\d+[^\n]*(\r?\n|$)/gim, "");
  // Collapse 3+ consecutive newlines into a double newline.
  s = s.replace(/\n{3,}/g, "\n\n");
  return s.trim();
}

// Sanitize item description/notes strings returned by the LLM. The model
// sometimes leaks markdown fragments ("**Description:**", stray asterisks,
// leading list dashes) into the structured JSON field. We want clean
// flowing sentences.
export function cleanDescription(input: string | undefined | null): string {
  if (!input) return "";
  let s = input;

  // Strip leading "Description:" / "Description -" / "Desc:" variants.
  s = s.replace(/^\s*\**\s*description\s*[:\-–]\s*/i, "");

  // Drop stray leading list dashes or bullets.
  s = s.replace(/^\s*[-*•·]\s+/, "");

  // Remove markdown emphasis markers left intact but keep their contents.
  // **bold** -> bold, *italic* -> italic, _word_ -> word
  s = s.replace(/\*\*(.+?)\*\*/g, "$1");
  s = s.replace(/(?<!\w)\*(.+?)\*(?!\w)/g, "$1");
  s = s.replace(/(?<!\w)_(.+?)_(?!\w)/g, "$1");

  // Collapse runs of whitespace (including newlines) into a single space so
  // a paragraph reads continuously rather than as a broken list.
  s = s.replace(/\s+/g, " ").trim();

  return s;
}
